import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Logo from '../components/Logo';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);


    // --- Email / Password login ---
    const handlePasswordLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await api.post('/auth/password-login', { email: email.trim(), password });
            const { accessToken, refreshToken, user } = res.data;
            login(accessToken, refreshToken, user, { authMethod: 'password' });
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Login failed. Please try again.');
        }
        setLoading(false);
    };

    // --- Google OAuth login ---
const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
        const decoded = jwtDecode(credentialResponse.credential);
        const res = await api.post('/auth/login', {
            email: decoded.email,
            name: decoded.name,
            google_sub: decoded.sub,
            picture: decoded.picture,
        });
        const { accessToken, refreshToken, user } = res.data;
        login(accessToken, refreshToken, user, { authMethod: 'google', picture: decoded.picture });
        navigate('/dashboard');
    } catch (err) {
        setError(err.response?.data?.error || 'Google sign-in failed. Please try again.');
    }
    setLoading(false);
};

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(255, 255, 255, 0.05)',
        color: '#ffffff',
        fontSize: '0.95rem',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s, background 0.2s',
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
                color: '#ffffff',
                padding: '20px',
                position: 'relative',
                overflow: 'hidden',
            }}>
            {/* Gradient orbs for visual interest */}
            <div style={{
                position: 'fixed',
                top: '-20%',
                right: '-10%',
                width: '500px',
                height: '500px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.12) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />
            <div style={{
                position: 'fixed',
                bottom: '-30%',
                left: '-10%',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.08) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />

            <div style={{ marginBottom: '32px', position: 'relative', zIndex: 10 }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                    <Logo size="large" dark={false} />
                </Link>
            </div>

            <style>{`
                .auth-form-card {
                    width: 100%;
                    max-width: 420px;
                    padding: 40px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 24px;
                    backdrop-filter: blur(20px);
                    position: relative;
                    z-index: 10;
                    box-sizing: border-box;
                }
                @media (max-width: 480px) {
                    .auth-form-card {
                        padding: 24px 20px;
                    }
                }
            `}</style>
            <div className="auth-form-card">
                <h2 style={{ margin: '0 0 8px', textAlign: 'center', fontSize: '1.75rem', fontWeight: 700 }}>
                    Welcome Back
                </h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', margin: '0 0 28px', fontSize: '0.95rem' }}>
                    Sign in to your You-Bee account
                </p>

                {/* Email / Password form */}
                <form onSubmit={handlePasswordLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Email or username
                        </label>
                        <input
                            type="text"
                            required
                            autoComplete="username"
                            placeholder="you@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            style={inputStyle}
                        />
                    </div>

                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                Password
                            </label>
                            <Link to="/forgot-password" style={{ fontSize: '0.8rem', color: '#ffc107', textDecoration: 'none' }}>
                                Forgot password?
                            </Link>
                        </div>
                        <input
                            type="password"
                            required
                            autoComplete="current-password"
                            placeholder="••••••••"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            style={inputStyle}
                        />
                    </div>

                    {error && (
                        <p style={{ margin: 0, color: '#ef4444', fontSize: '0.88rem', fontWeight: 500 }}>
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '14px',
                            background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                            color: '#000',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '1rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            marginTop: '4px',
                            boxShadow: '0 4px 20px rgba(255, 193, 7, 0.3)',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                        }}
                    >
                        {loading ? 'Signing in…' : 'Sign In'}
                    </button>
                </form>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.82rem' }}>or</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
                </div>

                {/* Google login */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError('Google sign-in failed. Please try again.')}
                        text="continue_with"
                        theme="filled_black"
                    />
                </div>

                {/* Register link */}
                <p style={{ textAlign: 'center', marginTop: '24px', marginBottom: 0, fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Don't have an account?{' '}
                    <Link to="/register" style={{ color: '#ffc107', fontWeight: 600, textDecoration: 'none' }}>
                        Create one
                    </Link>
                </p>
            </div>

        </div>
    );
}

export default Login;
